document.addEventListener('alpine:init', () => {
  Alpine.data('ctrHeroHomeSlider', () => ({
    swiper: null,
    swiperRetries: 0,
    boundBlockSelect: null,
    bufferTimeouts: [],

    init() {
      this.boundBlockSelect = this.handleBlockSelect.bind(this);
      document.addEventListener('shopify:block:select', this.boundBlockSelect);
      this.prepareVideos();
      this.initSwiper();
    },

    destroy() {
      this.bufferTimeouts.forEach((id) => window.clearTimeout(id));
      this.bufferTimeouts = [];

      if (this.boundBlockSelect) {
        document.removeEventListener('shopify:block:select', this.boundBlockSelect);
        this.boundBlockSelect = null;
      }
      this.destroySwiper();
    },

    getVideos() {
      return Array.from(this.$el.querySelectorAll('video.ctr-hero-home-slider__video'));
    },

    prepareVideos() {
      this.getVideos().forEach((video, index) => {
        if (video.dataset.videoSuppressed === 'true') return;

        video.muted = true;
        video.playsInline = true;
        video.setAttribute('playsinline', '');
        video.preload = 'auto';
        video.setAttribute('preload', 'auto');

        if (video.dataset.prepared === 'true') {
          this.bufferVideo(video, index);
          return;
        }

        video.dataset.prepared = 'true';
        video.classList.remove('is-ready');

        const warmFirstFrame = () => {
          if (video.dataset.warmed === 'true') return;
          video.dataset.warmed = 'true';

          try {
            if (video.currentTime < 0.05) {
              video.currentTime = 0.001;
            }
          } catch (error) {
            // Ignore seek errors while media is still opening.
          }

          const slide = video.closest('.swiper-slide');
          const isActive = slide && slide.classList.contains('swiper-slide-active');
          const isDuplicate = slide && slide.classList.contains('swiper-slide-duplicate');
          if (!isActive && (isDuplicate || !video.hasAttribute('autoplay'))) {
            video.pause();
          }
        };

        if (video.readyState >= 2) {
          warmFirstFrame();
        } else {
          video.addEventListener('loadeddata', warmFirstFrame, { once: true });
        }

        this.bufferVideo(video, index);
      });
    },

    bufferVideo(video, index) {
      if (!video || video.dataset.bufferStarted === 'true') return;
      if (video.dataset.videoSuppressed === 'true') return;

      video.dataset.bufferStarted = 'true';

      if (video.hasAttribute('autoplay') && !video.closest('.swiper-slide-duplicate')) {
        return;
      }

      const startBuffer = () => {
        if (video.dataset.videoSuppressed === 'true') return;
        if (video.readyState >= 2) return;

        const playPromise = video.play();
        if (playPromise && typeof playPromise.then === 'function') {
          playPromise
            .then(() => {
              const slide = video.closest('.swiper-slide');
              if (!slide || !slide.classList.contains('swiper-slide-active')) {
                video.pause();
              }
            })
            .catch(() => {});
        }
      };

      const delayBase = video.closest('.swiper-slide-duplicate') ? 800 : 0;
      const timeoutId = window.setTimeout(startBuffer, delayBase + Math.max(index, 1) * 250);
      this.bufferTimeouts.push(timeoutId);
    },

    initSwiper() {
      if (this.swiper) return;

      if (typeof Swiper === 'undefined') {
        if (this.swiperRetries >= 40) return;
        this.swiperRetries += 1;
        window.setTimeout(() => this.initSwiper(), 50);
        return;
      }

      const swiperEl = this.$el.querySelector('[ref="swiper"]');
      if (!swiperEl) return;

      const autoplayEnabled = this.$el.dataset.autoplay === 'true';
      const autoplayDelay = Number(this.$el.dataset.autoplayDelay) || 5000;
      const loopEnabled = this.$el.dataset.loop === 'true';
      const showDots = this.$el.dataset.showDots === 'true';
      const effect = this.$el.dataset.effect || 'fade';
      const paginationEl = this.$el.querySelector('[ref="pagination"]');
      const prevEl = this.$el.querySelector('[ref="prev"]');
      const nextEl = this.$el.querySelector('[ref="next"]');

      const config = {
        slidesPerView: 1,
        effect,
        loop: loopEnabled,
        grabCursor: true,
        watchSlidesProgress: true,
        on: {
          init: (swiper) => {
            this.prepareVideos();
            this.playActiveVideo(swiper);
          },
          slideChangeTransitionStart: (swiper) => {
            // Start the incoming video before the fade finishes.
            this.playActiveVideo(swiper);
          },
          slideChangeTransitionEnd: (swiper) => {
            // Pause only after the transition so both frames can crossfade cleanly.
            this.pauseInactiveVideos(swiper);
          },
        },
      };

      if (effect === 'fade') {
        config.fadeEffect = { crossFade: true };
      }

      if (autoplayEnabled) {
        config.autoplay = {
          delay: autoplayDelay,
          disableOnInteraction: true,
        };
      }

      if (showDots && paginationEl) {
        config.pagination = {
          el: paginationEl,
          clickable: true,
        };
      }

      if (prevEl && nextEl) {
        config.navigation = {
          prevEl,
          nextEl,
        };
      }

      this.swiper = new Swiper(swiperEl, config);
    },

    destroySwiper() {
      if (!this.swiper) return;
      this.swiper.destroy(true, true);
      this.swiper = null;
    },

    getActiveVideo(swiper) {
      const instance = swiper || this.swiper;
      if (!instance) return null;
      const activeSlide = instance.slides[instance.activeIndex];
      if (!activeSlide) return null;
      return activeSlide.querySelector('video.ctr-hero-home-slider__video');
    },

    playActiveVideo(swiper) {
      const video = this.getActiveVideo(swiper);
      if (video) this.playVideo(video);
    },

    pauseInactiveVideos(swiper) {
      const instance = swiper || this.swiper;
      if (!instance) return;

      const activeSlide = instance.slides[instance.activeIndex];

      instance.slides.forEach((slide) => {
        if (slide === activeSlide) return;
        const video = slide.querySelector('video.ctr-hero-home-slider__video');
        if (video) this.pauseVideo(video);
      });
    },

    playVideo(video) {
      if (!video || video.dataset.videoSuppressed === 'true') return;

      video.muted = true;
      video.playsInline = true;
      video.setAttribute('playsinline', '');

      const reveal = () => {
        video.classList.add('is-ready');
      };

      const tryPlay = () => {
        if (!video.paused && !video.ended) {
          reveal();
          return;
        }

        const playPromise = video.play();
        if (playPromise && typeof playPromise.then === 'function') {
          playPromise
            .then(() => {
              reveal();
            })
            .catch(() => {});
          return;
        }

        reveal();
      };

      if (video.readyState >= 2) {
        tryPlay();
        return;
      }

      // Wait for the already-requested preload. Do not call video.load() —
      // that resets the element and causes the blink.
      const onCanPlay = () => {
        video.removeEventListener('canplay', onCanPlay);
        video.removeEventListener('loadeddata', onCanPlay);
        tryPlay();
      };

      video.addEventListener('canplay', onCanPlay);
      video.addEventListener('loadeddata', onCanPlay);
    },

    pauseVideo(video) {
      if (!video) return;
      video.pause();
      // Keep the decoded frame (and is-ready). Resetting currentTime flashes the poster.
    },

    handleBlockSelect(event) {
      if (!this.swiper || !event.target) return;
      if (!this.$el.contains(event.target)) return;

      const slide = event.target.closest('.ctr-hero-home-slider__slide');
      if (!slide || slide.classList.contains('swiper-slide-duplicate')) return;

      const slides = Array.from(
        this.$el.querySelectorAll('.ctr-hero-home-slider__slide:not(.swiper-slide-duplicate)')
      );
      const index = slides.indexOf(slide);
      if (index < 0) return;

      if (this.swiper.params.loop) {
        this.swiper.slideToLoop(index);
      } else {
        this.swiper.slideTo(index);
      }
    },
  }));
});
